import React, { useMemo, useRef, useState } from 'react';
import { useGetAllPredefinedBasketsQuery } from '../../../store/services/api';
import BasketItem from '../BasketItem';
import { PredefinedBasketsSortingMap } from '../../../types/Baskets';
import DiscoverHeader from '../Header';
import { useSelector } from 'react-redux';
import { selectUserId } from '../../../store/slices/user';
import LoadingIcons from 'react-loading-icons';
import { AssetTypeFilter } from '../types';
import { getAssetType } from 'grts-utils/types/AssetType';

interface Props {
  credits: number | undefined;
  refresh: () => void;
  balances: Record<string, Record<string, number>> | undefined;
}

const PredefinedBaskets = (props: Props) => {
  const { credits, refresh, balances } = props;

  // Fetch all baskets
  const userId = useSelector(selectUserId);
  const { data, isLoading } = useGetAllPredefinedBasketsQuery(userId);
  const [activeBasket, setActiveBasket] = useState<string | undefined>();
  const [query, setQuery] = useState<string | undefined>();
  const [assetTypeFilter, setAssetTypeFilter] = useState<AssetTypeFilter>(
    AssetTypeFilter.All,
  );
  const scrollviewRef = useRef<HTMLDivElement | null>(null);

  const sortedData = useMemo(() => {
    if (!data) {
      return [];
    }

    const baskets = [...data.predefinedBaskets]
      .filter(
        (b) =>
          !query ||
          b.friendlyName.includes(query) ||
          b.baseAssets.includes(query),
      )
      // A basket matches a type filter if it contains at least one asset of that
      // type (baskets can be cross-assetType, e.g. crypto + stocks).
      .filter(
        (b) =>
          assetTypeFilter === AssetTypeFilter.All ||
          b.baseAssets
            .split(',')
            .some((a) => getAssetType(a.trim()) === assetTypeFilter),
      );

    baskets.sort(
      (a, b) =>
        (PredefinedBasketsSortingMap[a.friendlyName] ?? 0) -
        (PredefinedBasketsSortingMap[b.friendlyName] ?? 0),
    );

    return baskets;
  }, [data, query, assetTypeFilter]);

  const assetInfo = useMemo(() => {
    if (!data) {
      return {};
    }

    return data.assetInfo;
  }, [data]);

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        marginTop: 5,
        paddingLeft: 30,
        paddingRight: 30,
        overflowY: activeBasket ? 'hidden' : 'auto',
        marginBottom: 16,
      }}
    >
      <DiscoverHeader
        title={'Predefined Baskets'}
        query={query}
        setQuery={setQuery}
        isLoading={isLoading}
        credits={credits}
        assetTypeFilter={assetTypeFilter}
        setAssetTypeFilter={setAssetTypeFilter}
        assetTypeOptions={[
          { value: AssetTypeFilter.All, label: 'All' },
          { value: AssetTypeFilter.Crypto, label: 'Crypto' },
          { value: AssetTypeFilter.Stock, label: 'Stocks' },
          { value: AssetTypeFilter.Commodity, label: 'Commodities' },
        ]}
      />

      {isLoading && (
        <div
          style={{
            display: 'flex',
            flex: 1,
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <LoadingIcons.Grid width={60} height={60} />
        </div>
      )}

      {!!data && (
        <div
          ref={scrollviewRef}
          style={{
            display: 'grid',
            gridTemplateColumns: 'minmax(49%,1fr) minmax(49%,1fr)',
            gap: 16,
            position: 'relative',
          }}
        >
          {sortedData.map((item) => (
            <BasketItem
              key={item.id}
              item={item}
              parentRef={scrollviewRef}
              refresh={refresh}
              activeBasket={activeBasket}
              setActiveBasket={setActiveBasket}
              assetInfo={assetInfo}
              balances={balances}
              credits={credits}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default PredefinedBaskets;
